import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SoldPixelDetailModal } from "../components/SoldPixelDetailModal";
import {
  deleteSoldPixel,
  fetchAvailablePixels,
  fetchAdminPixelActions,
  fetchReservedPixels,
  fetchSoldPixels,
  releaseAllReservedPixels,
  releaseReservedPixel,
  undoAdminPixelAction,
  type AdminPixelAction,
  type ReservedPixel,
  type SoldPixel,
} from "../api";

type Tab = "available" | "reserved" | "pending" | "sold" | "actions";
type SoldSortColumn =
  | "pixel"
  | "buyer"
  | "location"
  | "region"
  | "country"
  | "status"
  | "sold_at";
type SortDirection = "asc" | "desc";

type SoldFilters = {
  buyer: string;
  country: string;
  region: string;
  status: string;
  location: string;
};

const EMPTY_SOLD_FILTERS: SoldFilters = {
  buyer: "",
  country: "",
  region: "",
  status: "",
  location: "",
};

const UNKNOWN_BUYER = "unknown";

function buyerKey(pixel: SoldPixel): string {
  return pixel.user_id == null ? UNKNOWN_BUYER : String(pixel.user_id);
}

function matchesRegexFilter(value: string, pattern: string): boolean {
  const trimmed = pattern.trim();
  if (!trimmed) return true;
  try {
    return new RegExp(trimmed, "i").test(value);
  } catch {
    return false;
  }
}

function compareStrings(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

function DeletePixelModal({
  pixel,
  busy,
  onCancel,
  onConfirm,
}: {
  pixel: SoldPixel;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (comment: string) => void;
}) {
  const [comment, setComment] = useState("");

  return (
    <div
      className="admin-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div className="admin-modal" role="dialog" aria-modal="true">
        <button
          type="button"
          className="admin-modal-close"
          aria-label="Close"
          disabled={busy}
          onClick={onCancel}
        >
          ×
        </button>
        <h2 className="admin-modal-title">Delete pixel {pixel.pixel}</h2>
        <div className="admin-modal-body">
          <p style={{ margin: 0, color: "#c5d4e0", fontSize: "0.92rem" }}>
            This makes the pixel available to buy again. Ownership metadata is
            snapshotted so the action can be undone later if the pixel is still
            free.
          </p>
          <p style={{ margin: 0, fontSize: "0.9rem", color: "#8ba3b8" }}>
            Current owner:{" "}
            <strong style={{ color: "#e8eef4" }}>
              {pixel.user_name || "—"}
              {pixel.user_email ? ` (${pixel.user_email})` : ""}
            </strong>
          </p>
          <label
            style={{ display: "block", marginBottom: "0.35rem", fontSize: "0.85rem" }}
          >
            Comment (required)
          </label>
          <textarea
            className="admin-input"
            rows={3}
            value={comment}
            disabled={busy}
            onChange={(e) => setComment(e.target.value)}
            placeholder="e.g. old debugging purchase, duplicate sale…"
            style={{ width: "100%", resize: "vertical" }}
          />
        </div>
        <div className="admin-modal-actions">
          <button
            type="button"
            className="admin-btn admin-btn-ghost"
            disabled={busy}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-danger"
            disabled={busy || !comment.trim()}
            onClick={() => onConfirm(comment.trim())}
          >
            Delete pixel
          </button>
        </div>
      </div>
    </div>
  );
}

function ownershipLabel(status?: SoldPixel["ownership_status"]): string {
  if (status === "available_again") return "Available again";
  return "Owned";
}

export function PixelsPage() {
  const [searchParams] = useSearchParams();
  const requestedBuyer = searchParams.get("buyer") || "";
  const [tab, setTab] = useState<Tab>(requestedBuyer ? "sold" : "reserved");
  const [available, setAvailable] = useState<ReservedPixel[]>([]);
  const [reserved, setReserved] = useState<ReservedPixel[]>([]);
  const [sold, setSold] = useState<SoldPixel[]>([]);
  const [actions, setActions] = useState<AdminPixelAction[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [detailPixel, setDetailPixel] = useState<SoldPixel | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SoldPixel | null>(null);
  const [soldFilters, setSoldFilters] = useState<SoldFilters>({
    ...EMPTY_SOLD_FILTERS,
    buyer: requestedBuyer,
  });
  const [sortColumn, setSortColumn] = useState<SoldSortColumn>("sold_at");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const load = useCallback(async () => {
    try {
      const [v, r, s, a] = await Promise.all([
        fetchAvailablePixels(),
        fetchReservedPixels(),
        fetchSoldPixels(200),
        fetchAdminPixelActions(200),
      ]);
      setAvailable(v);
      setReserved(r);
      setSold(s);
      setActions(a);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const buyerOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of sold) {
      const key = buyerKey(p);
      const label =
        key === UNKNOWN_BUYER
          ? "Unknown"
          : p.user_name && p.user_email
          ? `${p.user_name} (${p.user_email})`
          : p.user_name || p.user_email || `User #${p.user_id}`;
      map.set(key, label);
    }
    return [...map.entries()].sort((a, b) => compareStrings(a[1], b[1]));
  }, [sold]);

  const countryOptions = useMemo(() => {
    const set = new Set<string>();
    for (const p of sold) {
      if (p.country?.trim()) set.add(p.country.trim());
    }
    return [...set].sort(compareStrings);
  }, [sold]);

  const regionOptions = useMemo(() => {
    const set = new Set<string>();
    for (const p of sold) {
      if (p.region_name?.trim()) set.add(p.region_name.trim());
    }
    return [...set].sort(compareStrings);
  }, [sold]);

  const filteredSold = useMemo(() => {
    const filtered = sold.filter((p) => {
      if (soldFilters.buyer && buyerKey(p) !== soldFilters.buyer) return false;
      if (soldFilters.country && (p.country || "") !== soldFilters.country) {
        return false;
      }
      if (soldFilters.region && (p.region_name || "") !== soldFilters.region) {
        return false;
      }
      if (soldFilters.status) {
        const status = p.ownership_status || "owned";
        if (status !== soldFilters.status) return false;
      }
      if (
        !matchesRegexFilter(
          `${p.location || ""} ${p.country || ""}`,
          soldFilters.location,
        )
      ) {
        return false;
      }
      return true;
    });

    const dir = sortDirection === "asc" ? 1 : -1;
    filtered.sort((a, b) => {
      let cmp = 0;
      switch (sortColumn) {
        case "pixel":
          cmp = a.pixel - b.pixel;
          break;
        case "buyer":
          cmp = compareStrings(
            a.user_name || a.user_email || "",
            b.user_name || b.user_email || "",
          );
          break;
        case "location":
          cmp = compareStrings(a.location || "", b.location || "");
          break;
        case "region":
          cmp = compareStrings(a.region_name || "", b.region_name || "");
          break;
        case "country":
          cmp = compareStrings(a.country || "", b.country || "");
          break;
        case "status":
          cmp = compareStrings(
            a.ownership_status || "owned",
            b.ownership_status || "owned",
          );
          break;
        case "sold_at":
        default: {
          const at = a.bought_at ? Date.parse(a.bought_at) : 0;
          const bt = b.bought_at ? Date.parse(b.bought_at) : 0;
          cmp = at - bt;
          break;
        }
      }
      return cmp * dir;
    });
    return filtered;
  }, [sold, soldFilters, sortColumn, sortDirection]);

  const toggleSort = (column: SoldSortColumn) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection(column === "sold_at" ? "desc" : "asc");
    }
  };

  const sortIndicator = (column: SoldSortColumn) =>
    sortColumn === column ? (sortDirection === "asc" ? " ↑" : " ↓") : "";

  const freeOne = async (pixelId: number) => {
    setBusy(true);
    try {
      await releaseReservedPixel(pixelId);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const freeAll = async () => {
    if (!window.confirm("Release all reserved pixels?")) return;
    setBusy(true);
    try {
      await releaseAllReservedPixels();
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async (comment: string) => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteSoldPixel(deleteTarget.pixel, comment);
      setDeleteTarget(null);
      setDetailPixel(null);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const undoAction = async (actionId: number) => {
    if (
      !window.confirm(
        "Restore this pixel to the previous owner? This only works if the pixel is still available.",
      )
    ) {
      return;
    }
    setBusy(true);
    try {
      await undoAdminPixelAction(actionId);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const activeActionCount = actions.filter((a) => a.status === "active").length;
  const reservedPixels = reserved.filter((pixel) => pixel.status === 1);
  const pendingPixels = reserved.filter((pixel) => pixel.status === 2);
  const visibleReservedPixels = tab === "pending" ? pendingPixels : reservedPixels;
  const visibleInventoryPixels = tab === "available" ? available : visibleReservedPixels;

  return (
    <div>
      <h1 className="admin-page-title">Pixels</h1>
      <p className="admin-page-sub">
        Manage reserved inventory, review sales, and undo administrative deletes.
      </p>

      {error ? <p className="admin-error">{error}</p> : null}

      <div className="admin-tabs">
        <button
          type="button"
          className={`admin-tab${tab === "available" ? " active" : ""}`}
          onClick={() => setTab("available")}
        >
          Available ({available.length})
        </button>
        <button
          type="button"
          className={`admin-tab${tab === "reserved" ? " active" : ""}`}
          onClick={() => setTab("reserved")}
        >
          Reserved ({reservedPixels.length})
        </button>
        <button
          type="button"
          className={`admin-tab${tab === "pending" ? " active" : ""}`}
          onClick={() => setTab("pending")}
        >
          Payment pending ({pendingPixels.length})
        </button>
        <button
          type="button"
          className={`admin-tab${tab === "sold" ? " active" : ""}`}
          onClick={() => setTab("sold")}
        >
          Sold ({sold.length})
        </button>
        <button
          type="button"
          className={`admin-tab${tab === "actions" ? " active" : ""}`}
          onClick={() => setTab("actions")}
        >
          Admin actions ({activeActionCount})
        </button>
      </div>

      <div className="admin-toolbar">
        <button
          type="button"
          className="admin-btn admin-btn-ghost"
          onClick={() => void load()}
        >
          Refresh
        </button>
        {tab === "reserved" ? (
          <button
            type="button"
            className="admin-btn admin-btn-danger"
            disabled={busy || reservedPixels.length === 0}
            onClick={() => void freeAll()}
          >
            Free all reserved
          </button>
        ) : null}
        {tab === "sold" ? (
          <span className="admin-badge admin-badge-muted">
            {filteredSold.length} / {sold.length} shown
          </span>
        ) : null}
      </div>

      {tab === "available" || tab === "reserved" || tab === "pending" ? (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Pixel</th>
                <th>User</th>
                <th>Email</th>
                <th>Status</th>
                <th>Reserved at</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visibleInventoryPixels.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    {tab === "available"
                      ? "No persisted available pixels."
                      : tab === "pending"
                      ? "No pixels currently in the payment process."
                      : "No reserved pixels."}
                  </td>
                </tr>
              ) : (
                visibleInventoryPixels.map((p) => (
                  <tr key={p.pixel}>
                    <td>{p.pixel}</td>
                    <td>{p.user_name ?? "—"}</td>
                    <td>{p.user_email ?? "—"}</td>
                    <td>
                      <span className="admin-badge admin-badge-muted">
                        {p.status === 0
                          ? "Available"
                          : p.status === 2
                            ? "Payment pending"
                            : "Reserved"}
                      </span>
                    </td>
                    <td>
                      {p.updated
                        ? new Date(p.updated).toLocaleString()
                        : "—"}
                    </td>
                    <td>
                      {p.status === 1 ? (
                        <button
                          type="button"
                          className="admin-btn admin-btn-danger"
                          disabled={busy}
                          onClick={() => void freeOne(p.pixel)}
                        >
                          Free
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      {tab === "sold" ? (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>
                  <button
                    type="button"
                    className="admin-table-sort"
                    onClick={() => toggleSort("pixel")}
                  >
                    Pixel{sortIndicator("pixel")}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="admin-table-sort"
                    onClick={() => toggleSort("buyer")}
                  >
                    Buyer{sortIndicator("buyer")}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="admin-table-sort"
                    onClick={() => toggleSort("location")}
                  >
                    Location{sortIndicator("location")}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="admin-table-sort"
                    onClick={() => toggleSort("country")}
                  >
                    Country{sortIndicator("country")}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="admin-table-sort"
                    onClick={() => toggleSort("region")}
                  >
                    Region{sortIndicator("region")}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="admin-table-sort"
                    onClick={() => toggleSort("status")}
                  >
                    Status{sortIndicator("status")}
                  </button>
                </th>
                <th>
                  <button
                    type="button"
                    className="admin-table-sort"
                    onClick={() => toggleSort("sold_at")}
                  >
                    Sold at{sortIndicator("sold_at")}
                  </button>
                </th>
                <th />
              </tr>
              <tr className="admin-table-filters">
                <th />
                <th>
                  <select
                    className="admin-input admin-table-filter"
                    value={soldFilters.buyer}
                    onChange={(e) =>
                      setSoldFilters((f) => ({ ...f, buyer: e.target.value }))
                    }
                  >
                    <option value="">All buyers</option>
                    {buyerOptions.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </th>
                <th>
                  <input
                    className="admin-input admin-table-filter"
                    placeholder="Filter location…"
                    value={soldFilters.location}
                    onChange={(e) =>
                      setSoldFilters((f) => ({
                        ...f,
                        location: e.target.value,
                      }))
                    }
                  />
                </th>
                <th>
                  <select
                    className="admin-input admin-table-filter"
                    value={soldFilters.country}
                    onChange={(e) =>
                      setSoldFilters((f) => ({ ...f, country: e.target.value }))
                    }
                  >
                    <option value="">All countries</option>
                    {countryOptions.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </th>
                <th>
                  <select
                    className="admin-input admin-table-filter"
                    value={soldFilters.region}
                    onChange={(e) =>
                      setSoldFilters((f) => ({ ...f, region: e.target.value }))
                    }
                  >
                    <option value="">All regions</option>
                    {regionOptions.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                </th>
                <th>
                  <select
                    className="admin-input admin-table-filter"
                    value={soldFilters.status}
                    onChange={(e) =>
                      setSoldFilters((f) => ({ ...f, status: e.target.value }))
                    }
                  >
                    <option value="">All</option>
                    <option value="owned">Owned</option>
                    <option value="available_again">Available again</option>
                  </select>
                </th>
                <th />
                <th />
              </tr>
            </thead>
            <tbody>
              {filteredSold.length === 0 ? (
                <tr>
                  <td colSpan={8}>No sold pixels match the filters.</td>
                </tr>
              ) : (
                filteredSold.map((p) => {
                  const available = p.ownership_status === "available_again";
                  return (
                    <tr key={`${p.pixel}-${p.ownership_status}-${p.bought_at}`}>
                      <td>
                        <button
                          type="button"
                          className="admin-pixel-link"
                          onClick={() => setDetailPixel(p)}
                        >
                          {p.pixel}
                        </button>
                      </td>
                      <td>
                        <div>{p.user_name ?? "Unknown"}</div>
                        {p.user_email ? (
                          <div style={{ fontSize: "0.8rem", color: "#8ba3b8" }}>
                            {p.user_email}
                          </div>
                        ) : null}
                      </td>
                      <td>{p.location || "—"}</td>
                      <td>{p.country || "—"}</td>
                      <td>{p.region_name || "—"}</td>
                      <td>
                        <span
                          className={`admin-badge ${
                            available
                              ? "admin-badge-available"
                              : "admin-badge-live"
                          }`}
                        >
                          {ownershipLabel(p.ownership_status)}
                        </span>
                        {available && p.delete_comment ? (
                          <div
                            style={{
                              fontSize: "0.75rem",
                              color: "#8ba3b8",
                              marginTop: 4,
                            }}
                          >
                            {p.delete_comment}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        {p.bought_at
                          ? new Date(p.bought_at).toLocaleString()
                          : "—"}
                      </td>
                      <td>
                        {available ? (
                          p.admin_action_id && p.can_undo !== false ? (
                            <button
                              type="button"
                              className="admin-btn admin-btn-primary"
                              disabled={busy}
                              onClick={() =>
                                void undoAction(p.admin_action_id as number)
                              }
                            >
                              Undo
                            </button>
                          ) : (
                            "—"
                          )
                        ) : (
                          <button
                            type="button"
                            className="admin-btn admin-btn-danger"
                            disabled={busy}
                            onClick={() => setDeleteTarget(p)}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      {tab === "actions" ? (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Admin</th>
                <th>Pixel</th>
                <th>Previous owner</th>
                <th>Comment</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {actions.length === 0 ? (
                <tr>
                  <td colSpan={7}>No administrative pixel actions yet.</td>
                </tr>
              ) : (
                actions.map((a) => (
                  <tr key={a.action_id}>
                    <td>
                      {a.created_at
                        ? new Date(a.created_at).toLocaleString()
                        : "—"}
                    </td>
                    <td>
                      <div>{a.admin_name ?? "—"}</div>
                      {a.admin_email ? (
                        <div style={{ fontSize: "0.8rem", color: "#8ba3b8" }}>
                          {a.admin_email}
                        </div>
                      ) : null}
                    </td>
                    <td>{a.pixel_id ?? "—"}</td>
                    <td>
                      <div>{a.owner_name ?? "—"}</div>
                      {a.owner_email ? (
                        <div style={{ fontSize: "0.8rem", color: "#8ba3b8" }}>
                          {a.owner_email}
                        </div>
                      ) : null}
                    </td>
                    <td>{a.comment || "—"}</td>
                    <td>
                      {a.status === "active" ? (
                        <span className="admin-badge admin-badge-available">
                          Available again
                        </span>
                      ) : (
                        <span className="admin-badge admin-badge-muted">
                          Undone
                          {a.undone_at
                            ? ` · ${new Date(a.undone_at).toLocaleString()}`
                            : ""}
                        </span>
                      )}
                      {a.status === "active" && !a.can_undo ? (
                        <div
                          style={{
                            fontSize: "0.75rem",
                            color: "#ff8a8a",
                            marginTop: 4,
                          }}
                        >
                          Pixel re-assigned — delete current ownership to undo
                        </div>
                      ) : null}
                    </td>
                    <td>
                      {a.status === "active" && a.can_undo ? (
                        <button
                          type="button"
                          className="admin-btn admin-btn-primary"
                          disabled={busy}
                          onClick={() => void undoAction(a.action_id)}
                        >
                          Undo
                        </button>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      {detailPixel ? (
        <SoldPixelDetailModal
          pixel={detailPixel}
          onClose={() => setDetailPixel(null)}
          onDelete={
            detailPixel.ownership_status !== "available_again"
              ? () => {
                  setDeleteTarget(detailPixel);
                  setDetailPixel(null);
                }
              : undefined
          }
          onUndo={
            detailPixel.ownership_status === "available_again" &&
            detailPixel.admin_action_id
              ? () => {
                  const id = detailPixel.admin_action_id as number;
                  setDetailPixel(null);
                  void undoAction(id);
                }
              : undefined
          }
        />
      ) : null}

      {deleteTarget ? (
        <DeletePixelModal
          pixel={deleteTarget}
          busy={busy}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={(comment) => void confirmDelete(comment)}
        />
      ) : null}
    </div>
  );
}
